import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import Logo from '../../Assets/Img/Black-Logo.svg';
import { useCustomNavigation, useStores } from '../../Hooks';
import { authService } from '../../Services';
import { userService } from '../../Services/userService';
import styles from './index.module.scss';

import { useTranslation } from 'react-i18next';
import Button from '../../Components/ui/Button/Button';
import { Container } from '../../Components/ui/Container/Container';
import Link from '../../Components/ui/Link/Link';
import { PasswordField } from '../../Components/ui/PasswordField/PasswordField';
import Section from '../../Components/ui/Section/Section';
import TextField from '../../Components/ui/TextField/TextField';
import { Typography } from '../../Components/ui/Typography/Typography';
import CONSTANTS from '../../Constants';
import { useTrackScreenView } from '../../Hooks/use-track-screen-view';
import { Footer } from '../../Layout/Footer';
import { LoginSchema } from '../../Schema/LoginSchema';
import { getUrlForEnvironment } from '../../Utils/environment';

type FormData = yup.InferType<typeof LoginSchema>;

const LoginPage = () => {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormData>({
        resolver: yupResolver(LoginSchema) as any,
        mode: 'onTouched',
    });

    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { authStore, userStore } = useStores();
    const customNavigate = useCustomNavigation();
    const { t } = useTranslation();
    useTrackScreenView('screen_log_in_view');

    useEffect(() => {
        if (authStore.isLogin()) {
            customNavigate(CONSTANTS.SCREEN.HOME);
        }
    }, [authStore, customNavigate]);

    const getForgotPasswordURL = () => getUrlForEnvironment(CONSTANTS.FORGOT_PASSWORD_URL);
    const getCreateAccountURL = () => getUrlForEnvironment(CONSTANTS.CREATE_ACCOUNT_URL);

    const onSubmit = async (data: FormData) => {
        setIsLoading(true);
        try {
            const response = await authService.login(data.email, data.password);
            if (response) {
                authStore.setAuthInfo(response);
            }

            localStorage.setItem(CONSTANTS.LOCAL_STORAGE_KEYS.ACCESS_TOKEN, response.access_token);
            localStorage.setItem(
                CONSTANTS.LOCAL_STORAGE_KEYS.REFRESH_TOKEN,
                response.refresh_token
            );
        } catch (error) {
            if (error instanceof Error) {
                const errorMessages = JSON.parse(error.message)[0];
                if (errorMessages.includes('could not get user')) {
                    setErrorMessage('Incorrect email or password. Please try again.');
                } else if (errorMessages.includes('invalid login')) {
                    setErrorMessage('Incorrect email or password. Please try again.');
                } else if (errorMessages[0].includes('unconfirmed')) {
                    setErrorMessage('User is unconfirmed. Please confirm your email');
                } else if (errorMessages[0].includes('approved')) {
                    setErrorMessage('User is not approved');
                } else {
                    setErrorMessage(`An unknown error occurred: ${errorMessages}`);
                }
            } else {
                setErrorMessage(`An unknown error occurred`);
            }
            setIsLoading(false);
            return;
        }

        try {
            const user = await userService.getUser();

            if (!user) {
                throw new Error('ERR1');
            }
            userStore.setUser(user);
            customNavigate(CONSTANTS.SCREEN.HOME);
            setIsLoading(false);
        } catch (error) {
            setErrorMessage('An unexpected error occurred');
            localStorage.setItem(CONSTANTS.LOCAL_STORAGE_KEYS.ACCESS_TOKEN, '');
            localStorage.setItem(CONSTANTS.LOCAL_STORAGE_KEYS.REFRESH_TOKEN, '');
            setIsLoading(false);
        }
    };
    return (
        <main className={styles.loginPage}>
            <Section>
                <Container>
                    <div className={styles.container}>
                        <div className={styles.aside}>
                            <img src={Logo} alt="SpreeAI Logo" className={styles.logo} />
                            <Typography variant="heading2" component="h1">
                                Explore The SpreeAI Demo.
                            </Typography>
                            <Typography>
                                Discover a new dimension of online retail. Log in to access an
                                interactive demo designed to showcase a streamlined, forward-looking
                                shopping experience—built for clarity, ease, and impact.
                            </Typography>
                        </div>
                        <div className={styles.formContainer}>
                            <div>
                                {errorMessage && (
                                    <Typography variant="body" className={styles.errorMessage}>
                                        {errorMessage}
                                    </Typography>
                                )}
                                <form
                                    onSubmit={handleSubmit(onSubmit)}
                                    className={styles.form}
                                    id="login-form"
                                >
                                    <TextField
                                        required
                                        label="Email"
                                        type="email"
                                        {...register('email')}
                                        error={errors.email?.message}
                                    />
                                    <PasswordField
                                        label="Password"
                                        register={register}
                                        fieldName="password"
                                        error={errors.password?.message}
                                    />
                                </form>
                            </div>
                            <div className={styles.controls}>
                                <Button
                                    form="login-form"
                                    type="submit"
                                    disabled={isLoading}
                                    loading={isLoading}
                                >
                                    Log In
                                </Button>
                                <Button asChild variant="text">
                                    <Link
                                        to={getForgotPasswordURL()}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Forgot Password?
                                    </Link>
                                </Button>
                            </div>
                            <div className={styles.cta}>
                                <Typography variant="body" className="text-primary-black-75">
                                    No account?
                                </Typography>
                                <Button asChild variant="text">
                                    <Link
                                        to={getCreateAccountURL()}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Create One Now
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </Container>
            </Section>
            <Footer />
        </main>
    );
};

export default LoginPage;
